/**
 * Assistant message metadata footer (ticket 3525, upstream pin e3e7cc3f,
 * MIT): copy control plus timestamp, rendered inline on the terminal
 * assistant row and as a trailing meta row after trailing tool groups.
 *
 * @module TimelineAssistantMeta
 */

import { use } from "react";
import { cn } from "~/lib/utils";
import { formatTimestamp } from "../../timestampFormat";
import { resolveAssistantMessageCopyState } from "./MessagesTimeline.logic";
import { messageEndTime, type MessagesTimelineRow } from "./MessagesTimeline.rowTypes";
import { MessageCopyButton } from "./MessageCopyButton";
import { TimelineRowCtx } from "./TimelineRowContext";
import type { ChatMessage } from "../../types";

export function AssistantMessageMeta({
  className,
  message,
  showCopyButton,
  copyStreaming,
  alwaysVisible = false,
}: {
  className?: string;
  message: ChatMessage;
  showCopyButton: boolean;
  copyStreaming: boolean;
  alwaysVisible?: boolean;
}) {
  const ctx = use(TimelineRowCtx);

  return (
    <div
      className={cn(
        "flex items-center gap-2 text-xs tabular-nums transition-opacity duration-200",
        alwaysVisible
          ? "opacity-100"
          : "opacity-0 pointer-coarse:opacity-100 focus-within:opacity-100 group-hover/assistant:opacity-100",
        className,
      )}
    >
      <AssistantCopyButton
        message={message}
        showCopyButton={showCopyButton}
        streaming={copyStreaming}
      />
      {!message.streaming ? (
        <p className="text-muted-foreground/50 text-xs tabular-nums">
          {formatTimestamp(messageEndTime(message) ?? message.createdAt, ctx.timestampFormat)}
        </p>
      ) : null}
    </div>
  );
}

function AssistantCopyButton({
  message,
  showCopyButton,
  streaming,
}: {
  message: Extract<MessagesTimelineRow, { kind: "message" }>["message"];
  showCopyButton: boolean;
  streaming: boolean;
}) {
  const assistantCopyState = resolveAssistantMessageCopyState({
    text: message.text ?? null,
    showCopyButton,
    streaming,
  });

  if (!assistantCopyState.visible) {
    return null;
  }

  return (
    <MessageCopyButton
      text={assistantCopyState.text ?? ""}
      variant="ghost"
      ariaLabel="Copy assistant message"
    />
  );
}
